/**
 * Spectral-flux beat detector with adaptive threshold and a refractory window.
 * Operates entirely on main-thread frequency-domain data (an AnalyserNode's
 * frequency bins) and emits a `tick` plus diagnostics per call.
 *
 * The detector is deliberately diagnostic-friendly: every frame returns the
 * raw `flux`, the current adaptive `threshold`, the rolling BPM estimate, and
 * the time since the last tick, so the HUD can show *why* a beat did or
 * didn't fire, not just whether one did.
 *
 * Algorithm:
 *   1. Re-bin the input spectrum into BAND_COUNT bands (uniform groups over
 *      the input array). Input is expected to be the analyser's already
 *      log-binned 256-bin spectrum, so band 0 lands on the very lowest
 *      frequencies (kick / sub) and bands rise from there.
 *   2. Spectral flux = sum of positive per-band differences vs. the previous
 *      frame, divided by BAND_COUNT (so flux is in roughly the same 0..1
 *      range as the input).
 *   3. Maintain a ~1 s flux history. The adaptive threshold is
 *      `mean + THRESHOLD_K_STD * std + EPS`, which is more robust than a
 *      pure multiplier: it scales with how *variable* the recent flux is,
 *      not just how loud it is on average. A pure multiplier (`mean * c`)
 *      either misses beats during loud passages or fires constantly during
 *      quiet ones; mean+std handles both.
 *   4. Fire a tick when `flux > threshold` and we're past the refractory
 *      window since the last tick.
 */

export type BeatResult = {
  /** True for exactly one frame on each detected beat. */
  tick: boolean;
  /**
   * 0..1 normalized lead over threshold at the moment of the call.
   * For non-tick frames this is the live `(flux - threshold) / threshold`,
   * clamped. For tick frames it's the same value, which is necessarily > 0.
   */
  confidence: number;
  /** Raw spectral flux this frame (0..~1). */
  flux: number;
  /** Adaptive detection threshold this frame. */
  threshold: number;
  /**
   * Estimated BPM from the last few inter-beat intervals, or null if we
   * don't have enough history yet (need ≥ 2 beats inside the BPM window).
   */
  bpm: number | null;
  /** Milliseconds since the last beat tick, or null if none yet. */
  msSinceLastBeat: number | null;
};

const BAND_COUNT = 32;
/** History length in frames. ~1 s at 60 Hz; the detector is robust to ±20%. */
const HISTORY_LEN = 60;
/** Minimum gap between two ticks. ~270 ms ≈ 220 BPM ceiling. */
const REFRACTORY_MS = 270;
/** k * stddev added to the rolling mean for the adaptive threshold. */
const THRESHOLD_K_STD = 1.6;
/** Floor on the threshold so silence doesn't cause spurious ticks. */
const THRESHOLD_EPS = 0.003;
/** Window for the BPM estimator: only beats inside `now - this` count. */
const BPM_WINDOW_MS = 5000;
/** Reject implausible BPMs from noisy intervals. */
const MIN_BPM = 50;
const MAX_BPM = 220;

export class BeatDetector {
  private prevSpectrum = new Float32Array(BAND_COUNT);
  private fluxHistory = new Float32Array(HISTORY_LEN);
  private fluxHistoryFilled = 0;
  private cursor = 0;
  private lastTickTime = -Infinity;
  /** Ring buffer of recent beat times (seconds), for BPM estimation. */
  private beatTimes: number[] = [];

  /**
   * Feed the current spectrum (any length, will be re-binned to BAND_COUNT)
   * and the current absolute time in seconds. Returns the beat result.
   */
  update(spectrum: Float32Array, time: number): BeatResult {
    const banded = this.rebin(spectrum);

    let flux = 0;
    for (let i = 0; i < BAND_COUNT; i++) {
      const cur = banded[i] ?? 0;
      const prev = this.prevSpectrum[i] ?? 0;
      const d = cur - prev;
      if (d > 0) flux += d;
      this.prevSpectrum[i] = cur;
    }
    flux /= BAND_COUNT;

    this.fluxHistory[this.cursor] = flux;
    this.cursor = (this.cursor + 1) % HISTORY_LEN;
    if (this.fluxHistoryFilled < HISTORY_LEN) this.fluxHistoryFilled++;

    // Mean + stddev over filled history (not the full ring) so the very first
    // frames don't see a zero-padded floor that fires false beats.
    const n = this.fluxHistoryFilled;
    let sum = 0;
    for (let i = 0; i < n; i++) sum += this.fluxHistory[i] ?? 0;
    const mean = n > 0 ? sum / n : 0;

    let varSum = 0;
    for (let i = 0; i < n; i++) {
      const v = (this.fluxHistory[i] ?? 0) - mean;
      varSum += v * v;
    }
    const std = n > 1 ? Math.sqrt(varSum / (n - 1)) : 0;
    const threshold = mean + THRESHOLD_K_STD * std + THRESHOLD_EPS;

    const refractoryOk = (time - this.lastTickTime) * 1000 > REFRACTORY_MS;
    const tick = flux > threshold && refractoryOk;
    if (tick) {
      this.lastTickTime = time;
      this.beatTimes.push(time);
      // Trim to BPM window. Cheap because the array stays tiny (a few entries).
      const cutoff = time - BPM_WINDOW_MS / 1000;
      while (this.beatTimes.length > 0 && (this.beatTimes[0] ?? 0) < cutoff) {
        this.beatTimes.shift();
      }
    }

    const denom = Math.max(threshold, 1e-4);
    const confidence = clamp01((flux - threshold) / denom);
    const bpm = this.estimateBpm();
    const msSinceLastBeat =
      this.lastTickTime === -Infinity ? null : (time - this.lastTickTime) * 1000;

    return { tick, confidence, flux, threshold, bpm, msSinceLastBeat };
  }

  /** Reset all internal state. Useful when the audio source changes. */
  reset(): void {
    this.prevSpectrum.fill(0);
    this.fluxHistory.fill(0);
    this.fluxHistoryFilled = 0;
    this.cursor = 0;
    this.lastTickTime = -Infinity;
    this.beatTimes = [];
  }

  private estimateBpm(): number | null {
    const n = this.beatTimes.length;
    if (n < 2) return null;
    // Use median of recent inter-beat intervals to be robust against the
    // occasional missed/extra beat. Median needs >= 2 samples (== 3 beats);
    // for exactly 2 beats fall back to the single interval.
    const intervals: number[] = [];
    for (let i = 1; i < n; i++) {
      const a = this.beatTimes[i - 1];
      const b = this.beatTimes[i];
      if (a === undefined || b === undefined) continue;
      intervals.push(b - a);
    }
    if (intervals.length === 0) return null;
    intervals.sort((a, b) => a - b);
    const mid = intervals[Math.floor(intervals.length / 2)];
    if (mid === undefined || mid <= 0) return null;
    const bpm = 60 / mid;
    if (bpm < MIN_BPM || bpm > MAX_BPM) return null;
    return bpm;
  }

  private rebin(spectrum: Float32Array): Float32Array {
    const out = new Float32Array(BAND_COUNT);
    if (spectrum.length === 0) return out;
    const groupSize = Math.max(1, Math.floor(spectrum.length / BAND_COUNT));
    for (let b = 0; b < BAND_COUNT; b++) {
      let sum = 0;
      const start = b * groupSize;
      const end = Math.min(spectrum.length, start + groupSize);
      for (let i = start; i < end; i++) sum += spectrum[i] ?? 0;
      out[b] = sum / Math.max(1, end - start);
    }
    return out;
  }
}

function clamp01(v: number): number {
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}
